import type { ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { buyerSubmissionsApi, type BuyerSubmissionDetailDto } from "@/api/buyers";
import {
  Button,
  RECORD_RAIL_WIDTH,
  ResponsiveGrid,
  ResponsiveSplit,
  Skeleton,
  StatusBadge,
  Timestamp,
} from "@/design-system/components/ui";
import { formatCurrency } from "@/design-system/lib/format";
import { buyerSubmissionKeys } from "@/hooks/buyers/buyerKeys";
import { DetailSection, MilestoneList, PhaseLadder } from "../detailBits";
import { InlineQueryError } from "../queryStates";
import { describeSubmissionPhase } from "../submissionPhases";
import { computeEscalationReadiness } from "./escalationReadiness";
import { SubmissionActivityPreview } from "./SubmissionActivityPreview";
import { nextActionFor } from "./nextAction";
import type { TabKey } from "./submissionTabs";

/**
 * Overview — where the submission stands, and what is in the way of finishing
 * it. Nothing on this page changes anything.
 *
 * <p>🔴 <b>Layout Standard §4.1a: an Overview may summarise, reference and link;
 * it may not mutate or send.</b> The test is not whether a control is small or
 * whether this is the natural place for it — it is whether, after the click,
 * anything is different in our database or in someone's inbox. Six controls on
 * this page failed that test and have moved to the tabs that own their subject:
 * the escalation gate and its confirm, and the seller prequalification's link,
 * unlink, invite and nudge. Every one of them was an outbound send or a create;
 * the invite in particular put an email to a third party on the same surface a
 * reader lands on by default, and inside the record DRAWER, on top of whatever
 * they were already standing on.</p>
 *
 * <p>🔴 <b>What replaced them is a READING of the same answer, not a second
 * one.</b> The blocked-state summary below calls
 * <c>computeEscalationReadiness</c> — the pure function the escalation tab
 * renders as a table — off the identical <c>buyerSubmissionKeys.escalation</c>
 * query, served once from cache, and shows the server's composed strings
 * VERBATIM. One computation, one request, one wording. Two surfaces narrating
 * "what is blocking this" from separate logic drift, and this one would drift in
 * front of a specialist mid-call with a listing broker on the line.</p>
 *
 * <p>The adjacency that used to justify keeping seller prequalification here —
 * the escalation gate warns when it is missing, so the remedy has to be what the
 * eye lands on next — is PRESERVED rather than abandoned: both moved together,
 * and they are adjacent on the escalation tab in the same order. The argument
 * was always about the two being adjacent, never about them being on
 * Overview.</p>
 *
 * <p>What remains is §4.1 to the letter: identity facts, the progress ladder,
 * the milestone ladder, and a Recent Activity preview capped at five on the
 * rail. No call log, no tasks, no documents, no message thread, no note
 * composer.</p>
 */
export function SubmissionOverviewTab({
  s,
  onTabChange,
}: {
  s: BuyerSubmissionDetailDto;
  onTabChange: (tab: TabKey) => void;
}) {
  return (
    <div className="grid gap-space-5">
      {/* Both halves, always — the condensed chips AND the raw status badge.
          "Package" does not say whether the package is drafted or has gone out,
          so a chip on its own is a lossy read of the record. */}
      <PhaseLadder
        ladder={describeSubmissionPhase(s.status)}
        label="Submission progress"
        data-testid="submission-phase-ladder"
      />

      <ResponsiveSplit
        gap="lg"
        mainMinWidth="34rem"
        railWidth={RECORD_RAIL_WIDTH}
        railGrows={false}
        main={
          <div className="grid gap-space-5 min-w-0">
            <Vitals s={s} />
            <NextAction s={s} onTabChange={onTabChange} />
            <EscalationReadinessSummary s={s} onTabChange={onTabChange} />
          </div>
        }
        /* The right rail — permitted on any entity's Overview by Layout
           Standard §6.6, and justified here by §4.1's explicit sanction of a
           Recent Activity preview card. Milestones join it because they are a
           checklist rather than a workflow — something a reader glances at
           while doing the work in the main column, not something they act on.

           🔴 This said "the §7.5.4 rail". There is no §7.5.4, and there never
           was; see Layout Standard §9.1(a). The referent is §7.5 numbered
           item 4, which says the three named DEAL PANELS do not generalise —
           the opposite of a licence for a rail, and about a different thing
           entirely. The citation was written twice in this codebase and read
           as authoritative both times because a section number looks checked. */
        rail={
        <div className="grid gap-space-5 min-w-0">
          <DetailSection title="Milestones">
            {/*
              Counting ALWAYS reads the stamp, never the current status: a
              submission that later dies must not revoke credit already earned.
              Every rung is listed whether or not it has been reached, because
              what is still outstanding is the question the ladder answers.

              🔴 Every rung is READ-ONLY and links to the tab that owns it —
              Layout Standard §4.1a's resolution of the tension with §6.3. The
              door lives on the owning tab, which has the context, the permission
              gate and the confirmation; the rung is a summary and a way there.
              This is also what settles the NDA rendering in three places at
              once: the vitals tile says whether it is settled, this rung says
              when, and Paperwork is the only one of the three that can change
              it.
            */}
            <MilestoneList<TabKey>
              onNavigate={onTabChange}
              items={[
                { label: "Buyer approved", at: s.buyerApprovedAtUtc, to: "intro-email" },
                { label: "NDA signed", at: s.ndaSignedAtUtc, to: "paperwork" },
                {
                  label: "NDA sent to broker",
                  at: s.ndaSentToBrokerAtUtc,
                  to: "paperwork",
                },
                { label: "Broker responded", at: s.brokerRespondedAtUtc, to: "communications" },
                { label: "CIM received", at: s.cimReceivedAtUtc, to: "paperwork" },
                {
                  label: "Financials received",
                  at: s.financialsReceivedAtUtc,
                  to: "paperwork",
                },
                { label: "Call / meeting", at: s.meetingAtUtc, to: "calendar" },
                // The signed LOI is filed in Documents (the hand-off picks it from
                // there); under contract, the next door is escalation — the same
                // tabs the next-action prompt sends these two rungs to.
                { label: "Offer / LOI", at: s.loiAtUtc, to: "documents" },
                { label: "Under contract", at: s.underContractAtUtc, to: "escalation" },
                // Closing is what escalation leads to, and the escalation tab is
                // where the deal it became is read through.
                { label: "Closed", at: s.closedAtUtc, to: "escalation" },
              ]}
            />
          </DetailSection>

          <SubmissionActivityPreview
            submissionId={s.id}
            onSeeAll={() => onTabChange("activity")}
          />
        </div>
        }
      />
    </div>
  );
}

/**
 * What stands between this submission and being handed off — read-only, with a
 * way through to the tab that can do something about it.
 *
 * <p>🔴 <b>ONE readiness computation, shared with the escalation tab.</b> Not a
 * second narration of the same facts: this calls
 * <c>computeEscalationReadiness</c>, the pure function the escalation tab
 * renders as a table, off the SAME <c>buyerSubmissionKeys.escalation</c> query —
 * so React Query serves one request and the two surfaces cannot disagree about
 * what is in the way. Two places narrating that from separate logic drift, and
 * this one would drift in front of a specialist mid-call with a listing broker
 * on the line. Even the headline is the computation's, so they cannot word it
 * differently either.</p>
 *
 * <p>🔴 <b>The blocking messages are the SERVER's, verbatim.</b> The computation
 * attaches <c>blockedReason</c> to exactly one row — the first blocking
 * requirement, which is the one the server composed it for — and this renders it
 * as given. The pre-check and the refused POST are single-sourced precisely so
 * they cannot say different things.</p>
 *
 * <p>🔴 <b>No control here does anything.</b> Under §4.1a the confirm belongs on
 * the escalation tab, and so do the invite and the link that clear the advisory.
 * The one button changes the tab; the retry on a failed read re-issues a GET.
 * Reading and navigating are not "doing".</p>
 *
 * <p>It renders on every submission, not only nearly-ready ones: "what is in the
 * way" is the question this page exists to answer, and a section that appears
 * only at the end is a section nobody learns is there.</p>
 */
function EscalationReadinessSummary({
  s,
  onTabChange,
}: {
  s: BuyerSubmissionDetailDto;
  onTabChange: (tab: TabKey) => void;
}) {
  // The record already answers this, and it answers it without waiting: the
  // escalation stamps the status and the pointer in one SaveChanges. Skipping
  // the gate read for a submission that has been handed off also stops the page
  // asking a question that has been settled.
  const escalated =
    Boolean(s.escalation) || s.status === "EscalatedToDeal";

  const gate = useQuery({
    queryKey: buyerSubmissionKeys.escalation(s.id),
    queryFn: () => buyerSubmissionsApi.getEscalation(s.id),
    enabled: !escalated,
  });

  const readiness = computeEscalationReadiness(s, escalated ? null : gate.data);

  const open = (
    <div className="mt-space-3">
      <Button
        variant="secondary"
        size="sm"
        data-testid="submission-escalation-open"
        onClick={() => onTabChange("escalation")}
      >
        {readiness.alreadyEscalated ? "Open the deal" : "Open the escalation check"}
      </Button>
    </div>
  );

  if (readiness.alreadyEscalated) {
    return (
      <DetailSection title="Escalation">
        <p className="text-body m-0">{readiness.headline}</p>
        <p className="text-caption text-gray-mid mt-space-1 mb-0">
          Its progress is read through the deal, and the work happens there.
        </p>
        {open}
      </DetailSection>
    );
  }

  // "Checking…", never "ready" and never "blocked" — both are claims about an
  // answer nobody has received. The sentence is the computation's own, so even
  // the in-flight state is worded once.
  if (gate.isLoading) {
    return (
      <DetailSection title="Escalation readiness">
        <p className="text-body m-0">{readiness.headline}</p>
        <Skeleton className="h-12 w-full mt-space-3" />
      </DetailSection>
    );
  }

  // 🔴 Never "nothing is in the way" on a failed read. An absent answer and a
  // clear one look identical to a reader, and this is the surface they would
  // trust before telling a broker the hand-off is going ahead. The computation
  // reports `resolved: false` for exactly this reason; the query state is what
  // says whether that is because it is still coming or because it failed.
  if (gate.isError || !readiness.resolved) {
    return (
      <DetailSection title="Escalation readiness">
        <InlineQueryError
          what="the escalation checks"
          onRetry={() => void gate.refetch()}
          retrying={gate.isFetching}
        />
      </DetailSection>
    );
  }

  return (
    <DetailSection title="Escalation readiness">
      {/* 🔴 The headline is rendered, never composed. Composing a sentence from
          `blocking.length` here would put a second narration back on the page,
          which is the whole thing the shared computation exists to stop — and it
          would get the case wrong where the server refuses for a reason the rows
          do not model, where the count is zero and the answer is still no.

          The testid keys on the SERVER's verdict for the same reason: a row
          tally and `canEscalate` can disagree, and when they do the server is
          right. */}
      <p
        className="text-body m-0"
        data-testid={
          readiness.canEscalate
            ? "submission-escalation-ready"
            : "submission-escalation-blocked"
        }
      >
        {readiness.headline}
      </p>

      {/* Verbatim, server-composed — it names the record and who to ask, which a
          paraphrase would lose. Only the row the server actually wrote a message
          for carries one; the rest are named by the table on the tab, which has
          the room for a row per requirement. */}
      {readiness.blocking
        .filter((r) => r.message)
        .map((r) => (
          <div
            key={r.key}
            className="rounded-md border border-gray-mid bg-white-warm p-space-3 mt-space-3"
            data-testid="submission-escalation-blocker"
          >
            <p className="text-body m-0">{r.message}</p>
            <p className="text-caption text-gray-mid mt-space-1 mb-0">
              Cleared by: {r.owner}
            </p>
          </div>
        ))}

      {/* 🔴 ADVISORY, never a blocker — the seller prequalification depends on a
          third party Phoenix cannot compel, so the hand-off proceeds either way.
          Rendered apart from the blockers so the difference is visible, and it
          disables nothing here because there is nothing here to disable. */}
      {readiness.advisories.map((r) => (
        <div
          key={r.key}
          className="rounded-md border border-status-warning bg-white-warm p-space-3 mt-space-3"
          data-testid="submission-escalation-advisory"
        >
          <p className="text-body font-medium m-0">{r.label}</p>
          {r.message && (
            <p className="text-caption text-gray-mid mt-space-1 mb-0">{r.message}</p>
          )}
        </div>
      ))}

      {open}
    </DetailSection>
  );
}

/**
 * The five facts a specialist opens this record to check.
 *
 * <p>Money first because the whole pairing is a price negotiation, then the two
 * dates that decide whether anything is owed today, then whether the paperwork
 * that gates the broker conversation exists.</p>
 */
/**
 * The narrowest a vital may be, derived from what it has to HOLD — the way
 * `RECORD_CARD_MIN_WIDTH` and `RECORD_RAIL_WIDTH` are (Table & Tile §12.1).
 *
 * <p>The longest thing a tile carries that cannot break is the label
 * "Conversations": one word, `text-caption` `uppercase` `tracking-wide`
 * (0.04em, this project's token — not Tailwind's 0.025em), measured at
 * <b>121.5px</b> in Montserrat Regular 13px in Chromium. The NDA badge is the
 * other unbreakable (`StatusBadge` is `whitespace-nowrap`) and is narrower. So
 * the tile's content box must reach 121.5px, and the tile is that plus
 * `p-space-3` either side (32px) plus a 1px border either side: <b>155.5px</b>.</p>
 *
 * <p>The binding constraint is the price tile. Its label, "Deal structure
 * price", must hold in two lines, so the content box must fit "DEAL
 * STRUCTURE", measured at <b>126.8px</b> in the same face — a tile of
 * <b>160.8px</b>, which 10rem (160px) misses by 0.8px. 11rem (176px) was sized
 * for the label this tile carried before ("Provisional supported price", whose
 * "SUPPORTED PRICE" measured 135.6px) and still holds this one with 15.2px to
 * spare; it is kept rather than shaved to 10.5rem so the drawer's reflow
 * counts do not move.</p>
 *
 * <p>🔴 It is NOT `StatsBar`'s 9.5rem (152px): that is 3.5px short of this
 * label, and a tile too narrow for its label is a tile whose label paints past
 * its own padding — the defect this constant exists to end, at a scale nobody
 * would see until the row was five across again.</p>
 *
 * <p>🔴 Measure in the REAL face. The app loads Montserrat from Google Fonts,
 * which a sandboxed browser cannot reach, and the fallback face measures this
 * label at 118.3px — a figure that looks measured and is 3.2px short. Load
 * `public/fonts/Montserrat-Regular.woff2` through `FontFace` before trusting a
 * width.</p>
 */
const VITAL_TILE_MIN_WIDTH = "11rem";

function Vitals({ s }: { s: BuyerSubmissionDetailDto }) {
  const asking = s.askingPriceAtSubmission ?? s.listing?.askingPrice ?? null;

  return (
    /*
      The <section> stays for its landmark: `ResponsiveGrid` renders a <div>,
      and an `aria-label` on a div with no role is announced as nothing.
      `min-w-0` is §12.3's caller obligation — the grid itself carries one, but
      this section is a grid ITEM of the main column above it.

      🔴 `ResponsiveGrid`, never `xl:grid-cols-5`. This row used to be
      `grid-cols-2 md:grid-cols-3 xl:grid-cols-5`, which asks the VIEWPORT how
      many tiles fit — and inside the record drawer on a 1900px monitor the
      viewport said five while the main column was ~600px wide. Five tracks of
      ~106px, `min-w-0` on each tile, and "CONVERSATIONS" painted across the gap
      onto the Milestones card. The template below asks the container instead
      (Table & Tile §12.1); it is the same `cols={5}` StatsBar reflows on, with
      this row's own minimum. On the full page the main column is ~1152px and
      `max(11rem, (100% − 4·16px)/5)` is the same 217px track the old
      `grid-cols-5` produced, so the page is unchanged; in the drawer the count
      sheds to three, then two, and the tiles never leave their box.

      §12.2 says why this is not a container query: a card grid's failure mode
      under an unevaluated query is a silent collapse to one column.
    */
    <section aria-label="Submission vitals" data-testid="submission-vitals" className="min-w-0">
      <ResponsiveGrid
        cols={5}
        minColWidth={VITAL_TILE_MIN_WIDTH}
        // `sm` is 16px — the `gap-space-3` this row always had, passed as the
        // prop because the template reads the gap back out of `--phx-gap`.
        gap="sm"
        // The bare `grid` this replaces stretched its tiles by default;
        // `ResponsiveGrid` sets `items-start`, and `cn` is tailwind-merge, so
        // this later value wins the group and the tiles keep sharing an edge.
        className="items-stretch"
      >
      <Vital label="Asking" value={asking == null ? null : formatCurrency(asking)} />
      {/* A COMPLETED structure's price only — the server sends none before
          then, so an unfinished structure reads "—". */}
      <Vital
        label="Deal structure price"
        value={s.dealStructurePrice == null ? null : formatCurrency(s.dealStructurePrice)}
      />
      <Vital
        label="Next follow-up"
        value={
          s.nextFollowUpDate ? (
            <span className={s.isFollowUpOverdue ? "text-status-error" : undefined}>
              <Timestamp value={s.nextFollowUpDate} />
              {s.isFollowUpOverdue && " — overdue"}
            </span>
          ) : null
        }
      />
      <Vital label="NDA" value={<NdaVital s={s} />} />
      {/* Communications filed against this submission. Reads the count the
          server derives from the one log, rather than a second store's array. */}
      <Vital
        label="Conversations"
        value={
          !s.conversationCount ? null : String(s.conversationCount)
        }
      />
      </ResponsiveGrid>
    </section>
  );
}

/**
 * One tile.
 *
 * <p><c>min-w-0</c> on the tile, the label AND the value (Table & Tile §12.3):
 * a grid item defaults to <c>min-width: auto</c>, so without it a long value —
 * a currency figure, a date plus "— overdue" — would set its track's floor to
 * its own intrinsic width and push the row wider than its box.</p>
 *
 * <p>The label wraps at spaces and hyphens only — deliberately NO
 * <c>break-words</c>, which would let "CONVERSATIONS" break mid-word. It never
 * needs to: `VITAL_TILE_MIN_WIDTH` is derived from that word and from "DEAL
 * STRUCTURE", so the narrowest tile holds every word on one line, and "NEXT
 * FOLLOW-UP" and "DEAL STRUCTURE PRICE" wrap to two at most.</p>
 *
 * <p>A flex column with the value on <c>mt-auto</c>: when the row wraps and one
 * label takes two lines while its neighbour takes one, the values still sit on
 * one edge. Where every label is a single line — the full page — <c>mt-auto</c>
 * resolves to zero and <c>pt-space-1</c> is the 4px the value always had.</p>
 */
function Vital({ label, value }: { label: string; value: ReactNode }) {
  const empty = value === null || value === undefined || value === "";
  return (
    <div className="flex flex-col border border-border-light rounded-md bg-white-pure p-space-3 min-w-0">
      <div className="text-caption uppercase tracking-wide text-gray-mid min-w-0">{label}</div>
      <div className="text-small text-onyx mt-auto pt-space-1 min-w-0 break-words">
        {empty ? <span className="text-gray-mid">—</span> : value}
      </div>
    </div>
  );
}

/**
 * Whether the NDA question is SETTLED, and which way.
 *
 * <p>The list DTO's <c>ndaOnFile</c> is one bool and cannot say which — the
 * detail read carries both underlying fields, so this surface says the true
 * thing rather than the compressed one. "Not required" is a real answer, not a
 * missing signature.</p>
 */
function NdaVital({ s }: { s: BuyerSubmissionDetailDto }) {
  if (s.ndaSignedAtUtc) return <StatusBadge tone="ok">Signed</StatusBadge>;
  if (s.ndaNotRequiredReason) {
    return (
      <StatusBadge tone="muted" hint={s.ndaNotRequiredReason}>
        Not required
      </StatusBadge>
    );
  }
  return <StatusBadge tone="warn">Outstanding</StatusBadge>;
}

/**
 * One sentence saying what to do next, and a jump to the tab that carries the
 * control.
 *
 * <p>The decomposition into twelve tabs is what makes this necessary: the whole
 * record used to be one scroll, so "the next thing" was visible by accident.
 * With the work split by verb, a reader landing on Overview needs telling which
 * verb they are on.</p>
 */
function NextAction({
  s,
  onTabChange,
}: {
  s: BuyerSubmissionDetailDto;
  onTabChange: (tab: TabKey) => void;
}) {
  const action = nextActionFor(s);

  return (
    <section
      aria-label="Next action"
      data-testid="submission-next-action"
      className={
        action.tone === "warn"
          ? "rounded-md border border-status-warning bg-white-warm p-space-4"
          : "rounded-md border border-border-light bg-white-warm p-space-4"
      }
    >
      <p className="text-body font-medium m-0">{action.headline}</p>
      <p className="text-caption text-gray-mid mt-space-1 mb-0">{action.detail}</p>
      {action.tab && action.cta && (
        <div className="mt-space-3">
          <Button
            variant="secondary"
            size="sm"
            data-testid="submission-next-action-cta"
            onClick={() => onTabChange(action.tab as TabKey)}
          >
            {action.cta}
          </Button>
        </div>
      )}
    </section>
  );
}
